import { CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import "./i18n";
import accountRoutes from "./modules/account";
import adminRoutes from "./modules/admin_panel";
import alertRoutes from "./modules/alerts";
import AdminLayout from "./modules/admin_panel/shared/layout/AdminLayout";
import analyticsRoutes from "./modules/analytics";
import assetRoutes from "./modules/assets";
import { AssetsProvider } from "./modules/assets/contexts/AssetsContext";
import authRoutes from "./modules/authentication";
import ErrorBoundary from "./modules/common/components/ErrorBoundary";
import ScrollToTop from "./modules/common/components/ScrollToTop";
import { AuthProvider, useAuth } from "./modules/common/contexts/AuthContext";
import { ProjectProvider } from "./modules/common/contexts/ProjectContext";
import { useSettings } from "./modules/common/contexts/SettingsContext";
import { useTheme } from "./modules/common/contexts/ThemeContext";
import AppLayout from "./modules/common/layouts/AppLayout";
import PageNotFound from "./modules/common/pages/PageNotFound";
import contentRoutes from "./modules/content";
import dashboardRoutes from "./modules/dashboard";
import expenseRoutes from "./modules/expenses";
import financialRoutes from "./modules/financials";
import memberRoutes from "./modules/members";
import InviteAcceptPage from "./modules/members/pages/InviteAcceptPage";
import oauthRoutes from "./modules/oauth";
import payoutRoutes from "./modules/payouts";
import projectRoutes from "./modules/projects";
import Layout from "./modules/projects/layout/Layout";
import reportRoutes from "./modules/reports";
import settingsRoutes from "./modules/settings";
import sourceRoutes from "./modules/sources";
import supportRoutes from "./modules/support/routes";
import getTheme from "./theme";

const PrivateRoutes = () => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return authenticated ? <Outlet /> : <Navigate to="/login" />;
};

const AdminRoutes = () => {
  const { authenticated, loading, role } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!authenticated) {
    return <Navigate to="/login" />;
  }

  if (role !== "admin") {
    return <Navigate to="/" />;
  }

  return <Outlet />;
};

const renderRoutes = (routes) => {
  return routes.map((route, index) => {
    if (route.children) {
      return (
        <Route key={index} path={route.path} element={route.element}>
          {renderRoutes(route.children)}
        </Route>
      );
    } else {
      return (
        <Route
          key={index}
          path={route.path}
          element={route.element}
          index={route.index}
        />
      );
    }
  });
};

function App() {
  const { theme } = useTheme();
  const { customPrimaryColor } = useSettings();
  const [muiTheme, setMuiTheme] = useState(() =>
    getTheme(theme, customPrimaryColor),
  );

  useEffect(() => {
    setMuiTheme(getTheme(theme, customPrimaryColor));
  }, [theme, customPrimaryColor]);
  return (
    <Router>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        <AuthProvider>
          <ErrorBoundary>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<PrivateRoutes />}>
                <Route
                  path="/"
                  element={
                    <ProjectProvider>
                      <AssetsProvider>
                        <AppLayout />
                      </AssetsProvider>
                    </ProjectProvider>
                  }
                >
                  {renderRoutes([
                    ...dashboardRoutes,
                    ...analyticsRoutes,
                    ...memberRoutes,
                    ...reportRoutes,
                    ...accountRoutes,
                    ...contentRoutes,
                    ...sourceRoutes,
                    ...expenseRoutes,
                    ...payoutRoutes,
                    ...financialRoutes,
                    ...assetRoutes,
                    ...settingsRoutes,
                    ...supportRoutes,
                    ...alertRoutes,
                  ])}
                </Route>

                <Route path="/" element={<Layout />}>
                  {renderRoutes([...projectRoutes])}
                </Route>
              </Route>

              <Route path="/admin" element={<AdminRoutes />}>
                <Route path="/admin" element={<AdminLayout />}>
                  {renderRoutes([...adminRoutes])}
                </Route>
              </Route>

              {renderRoutes([...authRoutes, ...oauthRoutes])}

              <Route path="/invite/:inviteId" element={<InviteAcceptPage />} />

              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </MuiThemeProvider>
    </Router>
  );
}

export default App;
